import { FormElementMeta } from '@sierralabs/forms-core';
import { StateEventConfig, StateNodeType } from '@sierralabs/state-machine';
import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useRef
} from 'react';
import { FetchError } from '../../api/api';
import { useUserContext } from '../../context/UserContext';
import {
  DynamicFormContextState,
  useDynamicFormContext
} from '../../dynamic-forms/DynamicFormContext';
import { FormData } from '../../dynamic-forms/models/FormData';
import { useUpsertStudyParticipantEventForm } from '../../hooks/study-participant/useUpsertStudyParticipantEventForm';
import { FormTemplate } from '../../models/FormTemplate';
import { ParticipantEventLog } from '../../models/ParticipantEventLog';
import { reservedDataSourceKeys } from '../../common/hasCompletedFormTemplateFields';

export type StudyEventFormDataSourceProps = {
  studyParticipantId: number;
  participantEventId: number;
  participantEventLogId?: number;
  intendedStartDate?: Date;
  expireDayCount?: number;
  occurrenceCount: number;
  formTemplate: FormTemplate;
  disabled?: boolean;
  formData?: FormData<any>;
  onCreated?: (
    participantEventLog: ParticipantEventLog,
    formData: FormData<any>
  ) => void;
  onError?: (error: FetchError) => void;
};

const StudyEventFormDataSourceProvider: React.FC<
  PropsWithChildren & StudyEventFormDataSourceProps
> = props => {
  const {
    studyParticipantId,
    participantEventId,
    participantEventLogId,
    intendedStartDate,
    occurrenceCount,
    formTemplate,
    disabled,
    formData,
    children,
    onCreated,
    onError
  } = props;
  const { user, updateUserSession } = useUserContext();
  const {
    stateMachine,
    entitySchema,
    elementPlugins,
    activeStateNodes,
    formValues,
    changedFormValue,
    setIsSubmitting,
    setError,
    subscribe,
    unsubscribe,
    setTransitioningStateEvent
  } = useDynamicFormContext<any, any>();
  const onCreatedRef = useRef(onCreated);
  const onErrorRef = useRef(onError);
  const shouldResubmitRef = useRef(false);
  onCreatedRef.current = onCreated;
  onErrorRef.current = onError;
  const previousFormValueRef = useRef(changedFormValue);
  const entityIdRef = useRef(formData?.id);
  const stateMachineRef = useRef(stateMachine);
  const startDateRef = useRef(new Date());
  stateMachineRef.current = stateMachine;

  // get all form elements currently displayed in the DynamicForm component
  const formElementMetasRef = useRef<FormElementMeta<any, any>[]>([]);
  formElementMetasRef.current = activeStateNodes?.reduce(
    (elements, stateNode) => {
      elements.push(...(stateNode.meta?.elements || []));
      return elements;
    },
    [] as FormElementMeta<any, any>[]
  );

  const {
    upsertedEventData,
    isUpsertingEventData,
    upsertingEventDataError,
    upsertEventData
  } = useUpsertStudyParticipantEventForm();
  // handle state machine transitioning
  const finishTransition = useCallback(() => {
    setTransitioningStateEvent(event => {
      stateMachine?.transition(event?.name);
      return undefined;
    });
    setIsSubmitting(false);
  }, [setTransitioningStateEvent, setIsSubmitting, stateMachine]);
  // update entityId state after creating entity data
  useEffect(() => {
    if (upsertedEventData?.entityData?.id || formData?.id) {
      entityIdRef.current = upsertedEventData?.entityData?.id || formData?.id;
      // console.log('createdEntityData', createdEntityData);
      // setEntityId(createdEntityData?.id || formData?.id);
    }
  }, [formData, upsertedEventData?.entityData?.id]);
  // After updating the event form data, complete the state machine transition.
  // If creating a new form data entry, then notify parent component that a new
  // event data was created.
  useEffect(() => {
    if (upsertedEventData) {
      shouldResubmitRef.current = false;
      finishTransition();
    }
    if (!formData?.id && upsertedEventData?.entityData?.id) {
      const participantEvent = upsertedEventData.participantEvent;
      const participantEventLog = participantEvent.participantEventLogs[0];
      onCreatedRef.current?.(participantEventLog, upsertedEventData.entityData);
    }
  }, [finishTransition, formData?.id, upsertedEventData]);
  // Error handling
  useEffect(() => {
    if (upsertingEventDataError) {
      shouldResubmitRef.current = true;
      setError(upsertingEventDataError);
      onErrorRef.current?.(upsertingEventDataError);
    }
  }, [setError, upsertingEventDataError]);
  // Watch for form state transitions to initiate saving of the form data.
  // A server side call is only performed if for data changes are detected or
  // if setting the completion state.
  const subscriptionDependencyRef = useRef({
    changedFormValue,
    formValues,
    entitySchema
  });
  subscriptionDependencyRef.current = {
    changedFormValue,
    formValues,
    entitySchema
  };
  useEffect(() => {
    if (disabled) {
      return;
    }
    const submitEventData = async (
      context: DynamicFormContextState<any, any>,
      userId: number,
      statePath: string,
      isFinal: boolean
    ) => {
      const { entitySchema, formValues, changedFormValue } =
        subscriptionDependencyRef.current;
      const data = {} as any;
      for (const column of entitySchema?.columns || []) {
        // make sure to only upsert form data of actual entity schema fields
        let value = formValues[column.name];
        if (typeof value !== 'undefined') {
          // run any data transformations or file uploads from plugins before
          // upserting
          const formElementMeta = formElementMetasRef.current.find(
            e => e.dataSource === column.name
          );
          const plugin = elementPlugins.find(
            p => p.type === formElementMeta?.type
          );
          if (plugin?.onBeforeSave) {
            value = await plugin?.onBeforeSave(value, context);
          }
          // assign value for sending for upsert
          data[column.name] = value;
        }
      }
      // check if the column is one of the reserved data source keys
      for (const column of reservedDataSourceKeys) {
        if (formValues[column]) {
          data[column] = formValues[column];
        }
      }
      data.userId = userId;
      data.studyParticipantId = studyParticipantId;
      if (entityIdRef.current) {
        data.id = entityIdRef.current;
      }
      const duration = Math.round(
        (new Date().getTime() - startDateRef.current.getTime()) / 1000
      );
      if (!intendedStartDate) return;
      setIsSubmitting(true);
      await upsertEventData({
        studyParticipantId,
        participantEventId,
        participantEventLogId,
        intendedStartDate,
        occurrenceCount,
        isFinal,
        progress: 0,
        formTemplateId: formTemplate.id,
        version: formTemplate.version,
        statePath,
        data,
        duration
      });
      await updateUserSession();
    };
    const handleTransitionEvent = async (
      context: DynamicFormContextState<any, any>,
      event: StateEventConfig
    ) => {
      const { changedFormValue } = subscriptionDependencyRef.current;
      const stateMachine = stateMachineRef.current;
      const stateNode = stateMachine?.currentState?.path
        ? stateMachine?.getStateNode(stateMachine?.currentState?.path)
        : undefined;
      const isFinal = stateNode?.type === StateNodeType.Final;
      const statePath = stateMachine?.currentState?.path || '';
      if (
        user?.id &&
        ((changedFormValue &&
          previousFormValueRef.current !== changedFormValue) ||
          isFinal ||
          shouldResubmitRef.current === true)
      ) {
        previousFormValueRef.current = changedFormValue;
        await submitEventData(context, user.id, statePath, isFinal);
      } else {
        finishTransition();
      }
    };
    const handleFinalEvent = async (
      context: DynamicFormContextState<any, any>,
      event: StateEventConfig
    ) => {
      const stateMachine = stateMachineRef.current;
      const stateNode = stateMachine?.currentState?.path
        ? stateMachine?.getStateNode(stateMachine?.currentState?.path)
        : undefined;
      const isFinal = stateNode?.type === StateNodeType.Final;
      const statePath = stateMachine?.currentState?.path || '';
      // for backwards compatibility do not submit event data / set final state
      // if section (stateNode) has form elements that are required to complete.
      // Existing forms have "Final" state node type configured on sections
      // that contain form elements. The form will be marked completed upon
      // transitioning away from the section (see handleTransitionEvent)
      const hasRequiredFields = stateNode?.meta?.elemenets?.some(
        (e: any) => e.required
      );
      if (user?.id && isFinal && !hasRequiredFields) {
        await submitEventData(context, user.id, statePath, isFinal);
      }
    };
    subscribe('transition', handleTransitionEvent);
    subscribe('final', handleFinalEvent);
    return () => {
      unsubscribe('transition', handleTransitionEvent);
      unsubscribe('final', handleFinalEvent);
    };
  }, [
    disabled,
    elementPlugins,
    finishTransition,
    formTemplate.id,
    formTemplate.version,
    intendedStartDate,
    occurrenceCount,
    participantEventId,
    participantEventLogId,
    studyParticipantId,
    subscribe,
    unsubscribe,
    upsertEventData,
    user?.id,
    setIsSubmitting
  ]);

  return (
    <StudyEventFormDataSourceContext.Provider value={props}>
      {children}
    </StudyEventFormDataSourceContext.Provider>
  );
};

export const StudyEventFormDataSourceContext =
  createContext<StudyEventFormDataSourceProps>(null!);

export const useStudyEventFormDataSourceContext = () => {
  const context = useContext(StudyEventFormDataSourceContext);
  return context;
};

export default StudyEventFormDataSourceProvider;
